import { Document, GroupedDocument } from 'types/Invitation';

export type DocumentFolderNode = {
  type: 'folder';
  label: string;
  value: string;
  children: ( DocumentFolderNode | Document )[];
};

type DocumentInput = {
  documentoId: number;
  nombre: string;
  path: string;
};

export type TreeNode = {
  value: string;
  label: string;
  children?: TreeNode[];
};

type InternalNode = {
  value: string;
  label: string;
  children?: Record<string, InternalNode>;
};

export function buildDocumentTree(
  documents: DocumentInput[],
  ignorePrefixLevels = 0,
): TreeNode[] {
  const root: Record<string, InternalNode> = {};

  documents.forEach( ( doc ) => {
    const decodedPath = decodeURIComponent( doc.path );
    const parts = decodedPath.split( '/' ).filter( Boolean ).slice( ignorePrefixLevels );
    const fileName = doc.nombre;

    let currentLevel = root;
    let pathKey = '';

    parts.forEach( ( part, index ) => {
      const isFile = index === parts.length - 1;
      const label = isFile ? fileName : part;

      pathKey = pathKey ? `${pathKey}/${part}` : part;

      const value = isFile ? String( doc.documentoId ) : pathKey;

      if ( !currentLevel[pathKey] ) {
        currentLevel[pathKey] = {
          value,
          label,
          ...( isFile ? {} : { children: {} } ),
        };
      }

      if ( !isFile ) {
        currentLevel = currentLevel[pathKey].children!;
      }
    } );
  } );

  const normalize = ( nodeMap: Record<string, InternalNode> ):
  TreeNode[] => Object.values( nodeMap ).map( ( node ) => ( {
    value: node.value,
    label: node.label,
    ...( node.children ? { children: normalize( node.children ) } : {} ),
  } ) );

  return normalize( root );
}

export function groupDocumentsByFolderLevel(
  documents: Document[],
  level: number,
): GroupedDocument[] {
  const grouped: Record<string, GroupedDocument> = {};
  const noFolderKey = '__NO_FOLDER__';

  documents.forEach( ( doc ) => {
    let key = noFolderKey;
    let label: string | null = null;

    if ( doc.path ) {
      const decodedPath = decodeURIComponent( doc.path );
      const parts = decodedPath.split( '/' ).filter( Boolean );
      const folder = parts[level];

      if ( folder ) {
        key = folder;
        label = folder;
      }
    }

    if ( !grouped[key] ) {
      grouped[key] = {
        label,
        children: [],
      };
    }

    grouped[key].children.push( doc );
  } );

  const entries = Object.entries( grouped );
  const ordered = entries.sort( ( [a], [b] ) => {
    if ( a === noFolderKey ) return -1;
    if ( b === noFolderKey ) return 1;
    return a.localeCompare( b );
  } );

  return ordered.map( ( [, group] ) => group );
}
